import React from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { HubPrimaryAction } from "../shared/HubPrimaryAction";
import { PAGE_LAYOUT } from "../../layout/pageLayout";
import { DesktopKpiStrip } from "../shared/DesktopKpiStrip";
import {
  DeskEmpty,
  DeskPanel,
  DeskSearch,
  DeskTable,
  DeskTabs,
  DeskToolbar,
} from "../desktop/DeskTable";
import {
  CollectionsPanel,
  InvRow,
  InvoiceDueCell,
  InvoiceStatusPill,
  ReceivablesAgingPanel,
} from "./invoiceDesktopParts";
import {
  buildInvoiceHubFinancialMetrics,
  buildInvoiceHubRecentMetrics,
} from "../../helpers/invoiceHubDashboard";
import { buildInvoiceHubFilterOptions } from "../../helpers/invoiceHubFilters";
import { getInvoiceCollectionStatus } from "../../helpers/invoiceDueStatus";
import { formatInvoiceUpdatedAt } from "../../helpers/invoices";
import { formatCurrency } from "../../helpers/estimateTotals";

/**
 * Desktop Invoice Hub: KPI strip, status tabs + search, invoices table and a
 * side column with the existing recent-invoices / receivables summaries.
 * All numbers come from the same helpers the mobile dashboard uses.
 */
export function InvoiceHubDesktop({
  onBack,
  invoices,
  filtered,
  loading,
  statusFilter,
  onStatusFilter,
  searchTerm,
  onSearch,
  canCreate,
  onCreate,
  onOpen,
  showAccountsReceivable,
  arMetrics,
  arLoading,
  hubError,
  batch,
  children,
}) {
  const financial = buildInvoiceHubFinancialMetrics(invoices);
  const recent = buildInvoiceHubRecentMetrics(invoices, { onOpen, limit: 5 });
  const byKey = Object.fromEntries(financial.map((m) => [m.key, m.value]));
  const tabs = buildInvoiceHubFilterOptions(invoices);

  const columns = [
    ...(batch?.enabled
      ? [
          {
            key: "select",
            label: "",
            width: "52px",
            className: "dt-cell--actions",
            render: (invoice) => (
              <input
                type="checkbox"
                className="inv-check"
                checked={batch.selection.isSelected(invoice.invoice_id)}
                onChange={() => batch.selection.toggleItem(invoice.invoice_id)}
                aria-label={`Select ${invoice.invoice_number}`}
              />
            ),
          },
        ]
      : []),
    {
      key: "invoice",
      label: "Invoice",
      width: "19%",
      primary: true,
      render: (invoice) => (
        <span className="dt-item__text">
          <span className="dt-strong">{invoice.invoice_number}</span>
          <span className="dt-sub">{invoice.title || "Untitled Project"}</span>
        </span>
      ),
    },
    {
      key: "client",
      label: "Client",
      width: "17%",
      render: (invoice) => (
        <span className="dt-item__text">
          <span className="dt-strong">{invoice.clients?.client_name || "No client"}</span>
        </span>
      ),
    },
    {
      key: "status",
      label: "Status",
      width: "12%",
      render: (invoice) => <InvoiceStatusPill status={invoice.status} />,
    },
    {
      key: "due",
      label: "Due",
      width: "21%",
      render: (invoice) => <InvoiceDueCell invoice={invoice} />,
    },
    {
      key: "total",
      label: "Total",
      align: "right",
      width: "14%",
      render: (invoice) => {
        const total = Number(invoice.total_amount);
        return Number.isFinite(total) ? (
          <span className="dt-num">{formatCurrency(total)}</span>
        ) : (
          <span className="dt-muted">—</span>
        );
      },
    },
    {
      key: "updated",
      label: "Updated",
      width: "17%",
      render: (invoice) => (
        <span className="dt-muted dt-nowrap">{formatInvoiceUpdatedAt(invoice.updated_at)}</span>
      ),
    },
  ];

  return (
    <CrewPilotModuleShell
      title="Invoice Hub"
      subtitle="Invoices for your organization"
      onBack={onBack}
      layout={PAGE_LAYOUT.COMMAND}
      primary
      compact
      headerActions={
        <div className="cdh-actions">
          <DeskSearch value={searchTerm} onChange={onSearch} placeholder="Search invoices…" ariaLabel="Search invoices" />
          {canCreate ? (
            <HubPrimaryAction onClick={onCreate} label="New Invoice" ariaLabel="+ New Invoice" />
          ) : null}
        </div>
      }
    >
      <DesktopKpiStrip
        ariaLabel="Invoice financial summary"
        tiles={
          loading
            ? []
            : [
                { key: "draft", value: byKey.draft_value, label: "Draft Value", tone: "muted" },
                { key: "out", value: byKey.outstanding, label: "Outstanding Balance", tone: "gold" },
                { key: "paid", value: byKey.paid_total, label: "Paid Total", tone: "green" },
                { key: "count", value: String(byKey.invoice_count), label: "Total Invoices", tone: "blue" },
              ]
        }
      />

      <div className="inv-grid">
        <div className="inv-main">
          <DeskToolbar>
            <DeskTabs
              options={tabs}
              value={statusFilter === null ? "all" : statusFilter}
              onChange={(value) => onStatusFilter(value === "all" ? null : value)}
              ariaLabel="Filter invoices"
            />
            <span className="dt-toolbar__grow" />
            {batch?.enabled ? (
              <div className="inv-batch">
                <label>
                  <input
                    type="checkbox"
                    className="inv-check"
                    checked={batch.selection.allSelected}
                    onChange={batch.selection.toggleAll}
                    disabled={batch.busy}
                  />
                  Select all
                </label>
                {batch.actions.map((action) => {
                  const label =
                    typeof action.label === "function"
                      ? action.label(batch.selection.selectedCount)
                      : action.label;
                  const needsSelection = action.requiresSelection !== false;
                  return (
                    <button
                      key={action.id}
                      type="button"
                      className={`dt-btn${action.destructive ? " dt-btn--danger" : ""}`}
                      disabled={batch.busy || (needsSelection && batch.selection.selectedCount === 0)}
                      onClick={() => batch.onAction(action.id)}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </DeskToolbar>

          {hubError ? <div className="dt-error">{hubError}</div> : null}

          {loading ? (
            <p className="inv-note">Loading invoices…</p>
          ) : filtered.length === 0 ? (
            <DeskEmpty title="No invoices found" />
          ) : (
            <DeskTable
              testId="invoices-table"
              caption="Invoices"
              columns={columns}
              rows={filtered}
              rowKey={(invoice) => invoice.invoice_id}
              onOpen={onOpen}
              openLabel={(invoice) => `Open ${invoice.invoice_number}`}
              rowClassName={(invoice) =>
                getInvoiceCollectionStatus(invoice) === "overdue" ? "is-overdue" : ""
              }
            />
          )}
        </div>

        <aside className="inv-side" aria-label="Invoice summaries">
          <DeskPanel title="Recent Invoices">
            {loading ? (
              <p className="inv-note">Loading…</p>
            ) : recent.length === 0 ? (
              <p className="inv-note">No invoices yet.</p>
            ) : (
              <ul className="inv-rows">
                {recent.map((metric) => (
                  <InvRow
                    key={metric.key}
                    label={metric.label}
                    sub={metric.subtitle}
                    value={metric.value}
                    onClick={metric.onClick}
                  />
                ))}
              </ul>
            )}
          </DeskPanel>
          {showAccountsReceivable ? (
            <>
              <ReceivablesAgingPanel metrics={arMetrics} loading={arLoading} />
              <CollectionsPanel metrics={arMetrics} loading={arLoading} />
            </>
          ) : null}
        </aside>
      </div>
      {children}
    </CrewPilotModuleShell>
  );
}
